/**
 * Marvel Commercial LED Uploader Specifications
 * 
 * Commercial LED uploads for Marvel Stadium with:
 * - Tiled: 3 levels (FOP, L2, L3)
 * - 360: 3 levels with multiple segments each
 * - Interchange: Single asset for all levels
 * 
 * Note: Marvel has 3 levels vs MCG's 4 levels
 */

// ============================================================
// LED FORMAT OPTIONS
// ============================================================
export const LED_FORMATS = [
  { id: "tiled", label: "Tiled" },
  { id: "360", label: "360" },
  { id: "interchange", label: "Interchange" },
];

// ============================================================
// COMMON VIDEO SPEC (shared across LED types)
// ============================================================
const LED_VIDEO_COMMON = {
  allowed: true,
  frameRate: 50,
  extensions: ["mp4"],
  formats: ["AVC"],
  audio: { mode: "optional" },
  requiresAlpha: false,
  inspectAlpha: false,
};

const LED_IMAGE_COMMON = {
  allowed: true,
  extensions: ["png", "jpg", "jpeg"],
  requiresAlpha: false,
  inspectAlpha: false,
};

// Duration ranges
const COMMERCIAL_TILED_360_DURATION = 30; // Exact 30s for commercial tiled & 360
const INTERCHANGE_DURATION = 10; // Exact 10s

// ============================================================
// TILED LED SPECS (3 levels for Marvel)
// ============================================================
export const TILED_SPECS = {
  "MRV-TL-FOP": {
    label: "Marvel Tiled LED – Field of Play",
    uploadId: "MRV-TL-FOP",
    ledType: "tiled",
    level: "FOP",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MRV-TL-FOP\.[^.]+$/i,
    filenameHint: "Filename must end with _MRV-TL-FOP (before the extension)",
    exactDuration: COMMERCIAL_TILED_360_DURATION,
    
    dimensions: [{ width: 2370, height: 96 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 2370,
      height: 96,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 2370,
      height: 96,
    },
  },
  
  "MRV-TL-L2": {
    label: "Marvel Tiled LED – Level 2",
    uploadId: "MRV-TL-L2",
    ledType: "tiled",
    level: "L2",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MRV-TL-L2\.[^.]+$/i,
    filenameHint: "Filename must end with _MRV-TL-L2 (before the extension)",
    exactDuration: COMMERCIAL_TILED_360_DURATION,
    
    dimensions: [{ width: 2400, height: 90 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 2400,
      height: 90,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 2400,
      height: 90,
    },
  },
  
  "MRV-TL-L3": {
    label: "Marvel Tiled LED – Level 3",
    uploadId: "MRV-TL-L3",
    ledType: "tiled",
    level: "L3",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MRV-TL-L3\.[^.]+$/i,
    filenameHint: "Filename must end with _MRV-TL-L3 (before the extension)",
    exactDuration: COMMERCIAL_TILED_360_DURATION,
    
    dimensions: [{ width: 1850, height: 90 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 1850,
      height: 90,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 1850,
      height: 90,
    },
  },
};

// ============================================================
// 360 LED SPECS (3 levels for Marvel)
// ============================================================
export const THREE_SIXTY_SPECS = {
  "MRV-FOP": {
    label: "Marvel 360 LED – Field of Play",
    uploadId: "MRV-FOP",
    ledType: "360",
    level: "FOP",
    contentGroup: "groupedAssets",
    maxFiles: 5,
    requiredFiles: 5,
    exactDuration: COMMERCIAL_TILED_360_DURATION,
    filenameHint: "Filename must end with one of: _MRV-FOP-1, _MRV-FOP-2, _MRV-FOP-3, _MRV-FOP-4, _MRV-FOP-5",
    
    commonVideoSpec: LED_VIDEO_COMMON,
    imageSpec: LED_IMAGE_COMMON,
    
    segments: {
      "MRV-FOP-1": { width: 9000, height: 96 },
      "MRV-FOP-2": { width: 9000, height: 96 },
      "MRV-FOP-3": { width: 11520, height: 96 },
      "MRV-FOP-4": { width: 9000, height: 96 },
      "MRV-FOP-5": { width: 9000, height: 96 },
    },
  },
  
  "MRV-L2": {
    label: "Marvel 360 LED – Level 2",
    uploadId: "MRV-L2",
    ledType: "360",
    level: "L2",
    contentGroup: "groupedAssets",
    maxFiles: 6,
    requiredFiles: 6,
    exactDuration: COMMERCIAL_TILED_360_DURATION,
    filenameHint: "Filename must end with one of: _MRV-L2-1 through _MRV-L2-6",
    
    commonVideoSpec: LED_VIDEO_COMMON,
    imageSpec: LED_IMAGE_COMMON,
    
    segments: {
      "MRV-L2-1": { width: 6336, height: 80 },
      "MRV-L2-2": { width: 13344, height: 80 },
      "MRV-L2-3": { width: 13344, height: 80 },
      "MRV-L2-4": { width: 6336, height: 80 },
      "MRV-L2-5": { width: 6912, height: 80 },
      "MRV-L2-6": { width: 6912, height: 80 },
    },
  },
  
  "MRV-L3": {
    label: "Marvel 360 LED – Level 3",
    uploadId: "MRV-L3",
    ledType: "360",
    level: "L3",
    contentGroup: "groupedAssets",
    maxFiles: 4,
    requiredFiles: 4,
    exactDuration: COMMERCIAL_TILED_360_DURATION,
    filenameHint: "Filename must end with one of: _MRV-L3-1, _MRV-L3-2, _MRV-L3-3, _MRV-L3-4",
    
    commonVideoSpec: LED_VIDEO_COMMON,
    imageSpec: LED_IMAGE_COMMON,
    
    segments: {
      "MRV-L3-1": { width: 14910, height: 60 },
      "MRV-L3-2": { width: 14910, height: 60 },
      "MRV-L3-3": { width: 14910, height: 60 },
      "MRV-L3-4": { width: 14910, height: 60 },
    },
  },
};

// ============================================================
// INTERCHANGE LED SPEC (single asset for all levels)
// ============================================================
export const INTERCHANGE_SPEC = {
  "MRV-INT": {
    label: "Marvel Interchange LED – All Levels",
    uploadId: "MRV-INT",
    ledType: "interchange",
    contentGroup: "singleAsset",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MRV-INT\.[^.]+$/i,
    filenameHint: "Filename must end with _MRV-INT",
    exactDuration: INTERCHANGE_DURATION,
    
    dimensions: [{ width: 3156, height: 80 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 3156,
      height: 80,
      duration: INTERCHANGE_DURATION,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 3156,
      height: 80,
    },
  },
};

// ============================================================
// ALL SPECS BY FORMAT
// ============================================================
export const SPECS_BY_FORMAT = {
  tiled: TILED_SPECS,
  "360": THREE_SIXTY_SPECS,
  interchange: INTERCHANGE_SPEC,
};

// ============================================================
// ALL SPECS (flat object for lookup)
// ============================================================
export const ALL_SPECS = {
  ...TILED_SPECS,
  ...THREE_SIXTY_SPECS,
  ...INTERCHANGE_SPEC,
};

// ============================================================
// HELPER: Get segment ID from filename
// ============================================================
export function getSegmentIdFromFilename(filename, segments) {
  if (!segments) return null;
  
  const upper = filename.toUpperCase();
  
  return Object.keys(segments).find((segmentId) => {
    const re = new RegExp(`(?:^|[_])${segmentId}\\.[^.]+$`, "i");
    return re.test(upper);
  }) || null;
}

// ============================================================
// HELPER: Check if filename matches pattern
// ============================================================
export function validateFilename(filename, spec) {
  // For 360 specs with segments
  if (spec.segments) {
    const segmentId = getSegmentIdFromFilename(filename, spec.segments);
    return {
      valid: !!segmentId,
      segmentId,
      hint: spec.filenameHint,
    };
  }
  
  // For specs with explicit pattern
  if (spec.filenamePattern) {
    return {
      valid: spec.filenamePattern.test(filename),
      segmentId: null,
      hint: spec.filenameHint,
    };
  }
  
  // Fallback: check if ends with uploadId
  const re = new RegExp(`(?:^|[_])${spec.uploadId}\\.[^.]+$`, "i");
  return {
    valid: re.test(filename),
    segmentId: null,
    hint: `Filename must end with _${spec.uploadId}`,
  };
}

// ============================================================
// HELPER: Get dimensions for a file
// ============================================================
export function getDimensionsForFile(filename, spec) {
  // For 360 specs with segments
  if (spec.segments) {
    const segmentId = getSegmentIdFromFilename(filename, spec.segments);
    if (segmentId) {
      return spec.segments[segmentId];
    }
    return null;
  }
  
  // For specs with direct dimensions
  if (spec.dimensions?.length) {
    return spec.dimensions[0];
  }
  
  // Fallback to videoSpec dimensions
  if (spec.videoSpec?.width && spec.videoSpec?.height) {
    return { width: spec.videoSpec.width, height: spec.videoSpec.height };
  }
  
  return null;
}

// ============================================================
// HELPER: Build spec label for display
// ============================================================
export function buildSpecLabel(spec) {
  const parts = [];

  // Dimensions
  if (spec.segments) {
    const sizes = Object.values(spec.segments)
      .map((s) => `${s.width}×${s.height}`)
      .filter((v, i, a) => a.indexOf(v) === i); // unique
    parts.push(sizes.length > 3 ? "Various dimensions" : sizes.join(" / "));
  } else if (spec.dimensions?.length) {
    parts.push(spec.dimensions.map((d) => `${d.width}×${d.height}`).join(" / "));
  } else if (spec.videoSpec?.width && spec.videoSpec?.height) {
    parts.push(`${spec.videoSpec.width}×${spec.videoSpec.height}`);
  }

  // Video
  const videoSpec = spec.videoSpec || spec.commonVideoSpec;
  if (videoSpec) {
    parts.push("MP4");
    parts.push("H.264");
    if (videoSpec.frameRate) {
      parts.push(`${videoSpec.frameRate}fps`);
    }
    
    // Duration
    if (spec.exactDuration) {
      parts.push(`Duration: ${spec.exactDuration}s exactly`);
    } else if (spec.durationRange) {
      parts.push(`Duration: ${formatDuration(spec.durationRange.min)} - ${formatDuration(spec.durationRange.max)}`);
    }
    
    parts.push("Looping");
  }

  // Image
  if (spec.imageSpec?.allowed) {
    parts.push("Static JPG / PNG");
    parts.push("RGB");
  }

  return parts.join(" · ");
}

// ============================================================
// HELPER: Format duration for display
// ============================================================
export function formatDuration(seconds) {
  if (typeof seconds !== "number" || isNaN(seconds)) return "";
  
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  
  if (mins > 0) {
    return `${mins}:${String(secs).padStart(2, "0")}`;
  }
  return `${secs}s`;
}

// ============================================================
// METADATA WEBHOOK (Commercial - same as MCG commercial)
// ============================================================
export const METADATA_WEBHOOK = "https://hook.eu2.make.com/fgninplw8emfjkontq8csdhjxxc2g00v";

// ============================================================
// DEFAULT EXPORT
// ============================================================
const marvelCommercialLedSpecs = {
  LED_FORMATS,
  SPECS_BY_FORMAT,
  ALL_SPECS,
  TILED_SPECS,
  THREE_SIXTY_SPECS,
  INTERCHANGE_SPEC,
  getSegmentIdFromFilename,
  validateFilename,
  getDimensionsForFile,
  buildSpecLabel,
  formatDuration,
  METADATA_WEBHOOK,
};

export default marvelCommercialLedSpecs;
